import type { ReploSimpleProjectWithStats } from "schemas/generated/project";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import EditableProjectName from "@v1/components/dashboard/projects/EditableProjectName";
import ProjectImage from "@v1/components/dashboard/projects/GalleryProjectItem/ProjectImage";
import ShopifyButton from "@v1/components/dashboard/projects/GalleryProjectItem/ShopifyButton";
import StatSection from "@v1/components/dashboard/projects/GalleryProjectItem/StatSection";
import ProjectMenu from "@v1/components/dashboard/projects/ProjectMenu";
import { useEditedTimeString } from "@v1/hooks/useEditedTimeString";
import { getProjectName } from "@v2/utils/project-utils";
import { isShopifyIntegrationEnabled } from "schemas/utils";

type ProjectItemProps = {
  project: ReploSimpleProjectWithStats;
  onSelectProject: (e: React.MouseEvent) => void;
  className?: string;
  shouldShowEditedAt?: boolean;
  showWorkspaceColumn?: boolean;
  hideEllipsisButton?: boolean;
};

const GalleryProjectItem: React.FC<ProjectItemProps> = ({
  project,
  onSelectProject,
  className,
  shouldShowEditedAt = true,
  showWorkspaceColumn = false,
  hideEllipsisButton = false,
}) => {
  const editedTimeString = useEditedTimeString(
    project.lastEditedAt,
    project.createdAt,
  );
  const [isRenaming, setIsRenaming] = React.useState(false);
  const uniqueId = `project-${project.id}-${React.useId()}`;

  const handleStartRenaming = () => {
    setIsRenaming(true);
  };

  const handleFinishRenaming = () => {
    setIsRenaming(false);
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    if (!isRenaming) {
      onSelectProject(e);
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={twMerge(
        "border-0.5 border-border flex flex-col rounded-lg bg-white",
        className,
      )}
    >
      <ProjectImage
        projectId={project.id}
        workspaceId={project.ownerWorkspaceId}
        onSelectProject={onSelectProject}
      />
      <div className="flex flex-col gap-[8px] p-4">
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-between">
            <div className="overflow-hidden" onClick={handleTitleClick}>
              <EditableProjectName
                projectId={project.id}
                isRenaming={isRenaming}
                initialProjectName={getProjectName(project)}
                onFinishEditing={handleFinishRenaming}
                showTooltip
                className="text-default cursor-pointer truncate text-base font-medium"
              />
            </div>

            {!hideEllipsisButton && (
              <div className="-mr-2 flex flex-row items-center">
                <ProjectMenu
                  project={project}
                  allowsRenaming
                  onRenameOpen={handleStartRenaming}
                  isVertical
                />
              </div>
            )}
          </div>

          {showWorkspaceColumn && (
            <div
              className="text-placeholder mb-2 text-xs"
              aria-describedby={uniqueId}
            >
              {project.ownerWorkspace?.name ?? "-"}
            </div>
          )}
        </div>

        <StatSection elementCounts={project.elementCounts} />

        <div className="flex flex-row items-center justify-between">
          {shouldShowEditedAt && (
            <div
              className="text-placeholder text-xs"
              aria-describedby={uniqueId}
            >
              {editedTimeString}
            </div>
          )}

          <ShopifyButton
            isConnected={isShopifyIntegrationEnabled(project)}
            shopifyUrl={project.integrations?.shopify?.store.shopifyUrl}
            project={project}
          />
        </div>
      </div>
    </div>
  );
};

export default GalleryProjectItem;
